import React from "react";
import ReactECharts from "echarts-for-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as echarts from "echarts";
import { listSites } from "../../api/sites";
import { getSiteReport, regenerateReport } from "../../api/reports";

type SiteOption = { id: number; name: string };
type ToggleCardProps = { title: string; children: React.ReactNode; defaultOpen?: boolean; action?: React.ReactNode };

const ToggleCard: React.FC<ToggleCardProps> = ({ title, children, defaultOpen = false, action }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="rounded-lg border bg-white shadow-sm">
      <div className="flex items-center justify-between px-4 py-3 gap-3">
        <div className="flex items-center gap-3">
          <div className="text-sm font-semibold text-slate-900">{title}</div>
          <button
            className="text-xs text-slate-600 underline"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? "Collapse" : "Show"}
          </button>
        </div>
        {action ? <div className="flex items-center gap-2">{action}</div> : null}
      </div>
      {open && <div className="border-t px-4 py-3">{children}</div>}
    </div>
  );
};

const ratioTone = (value?: number | null): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return "text-slate-500";
  if (value >= 98) return "text-emerald-600";
  if (value >= 90) return "text-amber-600";
  return "text-rose-600";
};

const prTone = (value?: number | null): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return "text-slate-500";
  if (value >= 89) return "text-emerald-600";
  if (value >= 75) return "text-amber-600";
  return "text-rose-600";
};

const toneHex = (value: number | null | undefined, type: "pr" | "availability" | "ratio") => {
  if (value === null || value === undefined || Number.isNaN(value)) return "#94a3b8";
  if (type === "availability") {
    if (value >= 98) return "#10b981";
    if (value >= 90) return "#f59e0b";
    return "#ef4444";
  }
  // pr or ratio
  if (value >= 89) return "#10b981";
  if (value >= 75) return "#f59e0b";
  return "#ef4444";
};

const gaugeOption = (value: number | null | undefined, type: "pr" | "availability" | "ratio", max = 120) => {
  const safeVal = Math.max(0, Math.min(max, value ?? 0));
  const colorHex = toneHex(value ?? null, type);
  return {
    series: [
      {
        type: "gauge",
        startAngle: 210,
        endAngle: -30,
        min: 0,
        max,
        splitNumber: 0,
        axisLine: {
          lineStyle: {
            width: 8,
            color: [[1, "#e2e8f0"]],
          },
        },
        progress: {
          show: true,
          roundCap: true,
          width: 8,
          itemStyle: { color: colorHex },
        },
        pointer: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        detail: {
          valueAnimation: false,
          fontSize: 11,
          color: colorHex,
          formatter: (val: number) => `${val.toFixed(1)}%`,
          offsetCenter: [0, "55%"],
        },
        data: [{ value: safeVal }],
      },
    ],
  };
};

const PDF_BLUE: [number, number, number] = [0, 82, 155];
const PAGE_MARGIN_PT = 36; // 0.5 inch

const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));

const chartToDataUrl = async (option: any, width = 900, height = 420) => {
  if (!option) return null;
  const safeOption = { ...option, animation: false };
  const el = document.createElement("div");
  el.style.position = "fixed";
  el.style.left = "-99999px";
  el.style.width = `${width}px`;
  el.style.height = `${height}px`;
  document.body.appendChild(el);
  const instance = echarts.init(el, undefined, { renderer: "canvas", width, height });
  instance.setOption(safeOption, true);
  await sleep(80);
  instance.resize({ width, height });
  const dataUrl = instance.getDataURL({ pixelRatio: 1.5, backgroundColor: "#ffffff" });
  instance.dispose();
  el.remove();
  return dataUrl;
};

const timeToMinutes = (timeStr?: string | null, fallback?: number | null) => {
  if (!timeStr) return fallback ?? null;
  const [hh, mm] = timeStr.split(":").map((v) => Number(v));
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return fallback ?? null;
  return hh * 60 + mm;
};

const percentLabel = (val: any, precision = 2) => (val === null || val === undefined ? "—" : `${Number(val).toFixed(precision)}%`);
const numberLabel = (val: any, precision = 1, suffix = "") => (val === null || val === undefined ? "—" : `${Number(val).toFixed(precision)}${suffix}`);
const groupedNumberLabel = (val: any, precision = 0) => {
  if (val === null || val === undefined) return "—";
  return Number(val).toLocaleString(undefined, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
};

const GREEN: [number, number, number] = [16, 185, 129];
const ORANGE: [number, number, number] = [245, 158, 11];
const RED: [number, number, number] = [239, 68, 68];
const SLATE: [number, number, number] = [51, 65, 85];

const colorForPr = (val?: number | null): [number, number, number] => {
  if (val === null || val === undefined || Number.isNaN(val)) return SLATE;
  if (val >= 89) return GREEN;
  if (val >= 75) return ORANGE;
  return RED;
};

const colorForAvailability = (val?: number | null): [number, number, number] => {
  if (val === null || val === undefined || Number.isNaN(val)) return SLATE;
  if (val >= 98) return GREEN;
  if (val >= 90) return ORANGE;
  return RED;
};

const MetricLine: React.FC<{ label: string; value: any; unit?: string; tone?: "warn" | "crit" }> = ({ label, value, unit, tone }) => {
  const toneClass = tone === "warn" ? "text-amber-600" : tone === "crit" ? "text-red-600" : "text-slate-900";
  const display = value === null || value === undefined || value === "undefined" ? "—" : value;
  return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-600">{label}:</span>
      <span className={`font-semibold ${toneClass}`}>
        {display}
        {unit ? ` ${unit}` : ""}
      </span>
    </div>
  );
};

const Kpi: React.FC<{ label: string; value: any; suffix?: string; precision?: number }> = ({ label, value, suffix, precision = 2 }) => {
  const formatted =
    value === null || value === undefined || Number.isNaN(value) ? "—" : typeof value === "number" ? Number(value).toFixed(precision) : value;
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/70 px-4 py-3 shadow-sm">
      <div className="flex items-baseline justify-between gap-2 text-sm font-medium text-slate-700">
        <span>{label}:</span>
        <span className="text-lg font-semibold text-slate-900">
          {formatted}
          {suffix ? ` ${suffix}` : ""}
        </span>
      </div>
    </div>
  );
};

const ticketStatusTone: Record<string, string> = {
  OPEN: "bg-amber-50 text-amber-800 ring-1 ring-amber-100",
  IN_PROGRESS: "bg-blue-50 text-blue-800 ring-1 ring-blue-100",
  RESOLVED: "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-100",
  CLOSED: "bg-slate-100 text-slate-700 ring-1 ring-slate-200",
};

const ticketSeverityTone: Record<number, string> = {
  1: "bg-yellow-50 text-gray-700 ring-1 ring-gray-200",
  2: "bg-red-50 text-red-800 ring-1 ring-red-100",
};

const severityLabelMap: Record<number, string> = {
  1: "Warning",
  2: "Critical",
};

const formatStatusLabel = (statusRaw: string) => {
  if (!statusRaw) return "Unknown";
  const pretty = statusRaw.replace(/_/g, " ").toLowerCase();
  return pretty.replace(/\b\w/g, (ch) => ch.toUpperCase());
};

const formatDateTime = (iso?: string | null) => {
  if (!iso) return "—";
  const dt = new Date(iso);
  if (Number.isNaN(dt.valueOf())) return "—";
  return dt.toLocaleString();
};

type ReportTicketDisplay = {
  id: number | string;
  statusNormalized: string;
  statusLabel: string;
  severity?: number | null;
  severityLabel?: string | null;
  openedLabel: string;
  closedLabel?: string;
  message?: string | null;
  siteLabel: string;
  transformerLabel: string;
  inverterLabel: string;
};

const ReportTicketCard: React.FC<{ ticket: ReportTicketDisplay }> = ({ ticket }) => {
  const statusClass = ticketStatusTone[ticket.statusNormalized] || "bg-slate-100 text-slate-700 ring-1 ring-slate-200";
  const severityClass =
    ticket.severity != null && ticketSeverityTone[ticket.severity] ? ticketSeverityTone[ticket.severity] : "bg-slate-50 text-slate-700 ring-1 ring-slate-200";

  return (
    <div className="flex items-start gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${statusClass}`}>
        {ticket.id ?? "—"}
      </div>

      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-900">Ticket #{ticket.id ?? "—"}</div>
        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs sm:text-sm text-slate-700">
          <span>
            <span className="font-semibold text-slate-900">Site:</span> {ticket.siteLabel}
          </span>
          <span>
            <span className="font-semibold text-slate-900">Transformer:</span> {ticket.transformerLabel}
          </span>
          <span>
            <span className="font-semibold text-slate-900">Inverter:</span> {ticket.inverterLabel}
          </span>
        </div>
        <div className="mt-1 text-xs text-slate-600">
          Opened: {ticket.openedLabel}
          {ticket.closedLabel ? ` | Closed: ${ticket.closedLabel}` : ""}
        </div>
        <div className="mt-2 text-sm leading-snug text-slate-800">{ticket.message || "No description provided."}</div>
      </div>

      <div className="flex flex-col items-end gap-2">
        <span className={`rounded-full px-2 py-1 text-[11px] font-semibold ${statusClass}`}>{ticket.statusLabel}</span>
        {ticket.severityLabel && (
          <span className={`rounded-full px-2 py-1 text-[11px] font-semibold ${severityClass}`}>{ticket.severityLabel}</span>
        )}
      </div>
    </div>
  );
};

const TicketGroup: React.FC<{ title: string; tickets: ReportTicketDisplay[] }> = ({ title, tickets }) => (
  <div className="space-y-3">
    <div className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-700">
      <span>{title}</span>
      <span className="text-[11px] font-medium text-slate-500">{tickets.length}</span>
    </div>
    {tickets.length ? (
      <div className="space-y-3">
        {tickets.map((ticket) => (
          <ReportTicketCard key={`${title}-${ticket.id}`} ticket={ticket} />
        ))}
      </div>
    ) : (
      <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-500">No tickets in this group.</div>
    )}
  </div>
);

const ReportsPage: React.FC = () => {
  const [sites, setSites] = React.useState<SiteOption[]>([]);
  const [selectedSite, setSelectedSite] = React.useState<string>("");
  const [date, setDate] = React.useState<string>(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = React.useState(false);
  const [regenerating, setRegenerating] = React.useState(false);
  const [pdfGenerating, setPdfGenerating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [warning, setWarning] = React.useState<string | null>(null);
  const [report, setReport] = React.useState<any>(null);
  const [status, setStatus] = React.useState<string>("");
  const [chartKey, setChartKey] = React.useState<"production" | "transformers" | "temperatures" | "setpoint">("production");
  const [hourlyPerformanceFilter, setHourlyPerformanceFilter] = React.useState<string>("all");
  const [inverterSort, setInverterSort] = React.useState<{ key: "inverter" | "pr" | "availability" | "energy" | "ratio" | "start" | "stop"; dir: "asc" | "desc" }>({
    key: "inverter",
    dir: "asc",
  });

  React.useEffect(() => {
    const loadSites = async () => {
      try {
        const data = await listSites();
        setSites(data || []);
        if (data && data.length) {
          setSelectedSite(String(data[0].id));
        }
      } catch (err) {
        setError("Failed to load sites");
      }
    };
    loadSites();
  }, []);

  React.useEffect(() => {
    if (!selectedSite || !date) return;
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite, date]);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    setWarning(null);
    try {
      const data = await getSiteReport(selectedSite, date);
      setReport(data.payload);
      setStatus(data.status);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setReport(null);
        setStatus("missing");
        setError("No report found for this date.");
      } else {
        setError("Failed to load report");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    if (!selectedSite || !date) return;
    setRegenerating(true);
    setError(null);
    setWarning(null);
    try {
      const data = await regenerateReport(selectedSite, date);
      setReport(data.payload);
      setStatus(data.status);
      if (data?.warning) {
        setWarning(data.warning);
      }
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setWarning(err?.response?.data?.detail || "Report cannot be regenerated because per-minute performance data is unavailable.");
      } else {
        setError("Failed to regenerate report");
      }
    } finally {
      setRegenerating(false);
    }
  };

  const meta = report?.meta || {};
  const kpis = report?.kpis || {};
  const siteName = meta.site_name || (meta as any).siteName || "Site";

  const ticketGroups = React.useMemo(() => {
    const tickets = report?.issues?.tickets || [];
    if (!tickets.length) return { active: [], closed: [], other: [] };

    const inverterLookup: Record<number, { inverter?: string; transformer?: string; arrayId?: number; arrayName?: string }> = {};
    (report?.inverters || []).forEach((inv: any) => {
      const invId = Number(inv.array_inverter_id);
      if (Number.isFinite(invId)) {
        inverterLookup[invId] = { inverter: inv.inverter, transformer: inv.transformer, arrayId: inv.array_id, arrayName: inv.array_name };
      }
    });

    const transformerByArrayId: Record<number, string> = {};
    (report?.inverters || []).forEach((inv: any) => {
      if (inv.array_id !== undefined && inv.array_id !== null && (inv.array_name || inv.transformer) && !transformerByArrayId[inv.array_id]) {
        transformerByArrayId[inv.array_id] = inv.array_name || inv.transformer;
      }
    });

    const formatted = tickets.map((t: any) => {
      const statusNormalized = (t.status || "").toString().toUpperCase() || "UNKNOWN";
      const inverterInfo = inverterLookup[t.array_inverter_id ?? -1];
      const transformerName = inverterInfo?.arrayName || inverterInfo?.transformer || (t.array_id != null ? transformerByArrayId[t.array_id] : null);
      return {
        ...t,
        statusNormalized,
        statusLabel: formatStatusLabel(statusNormalized),
        severityLabel: severityLabelMap[t.severity as number] || null,
        siteLabel: siteName,
        inverterLabel: inverterInfo?.inverter || (t.array_inverter_id ? `Inverter ${t.array_inverter_id}` : "—"),
        transformerLabel: transformerName || (t.array_id ? `Array ${t.array_id}` : "—"),
        openedLabel: formatDateTime(t.opened_at),
        closedLabel: t.closed_at ? formatDateTime(t.closed_at) : "",
        message: t.message || "No description provided.",
      } as ReportTicketDisplay;
    });

    const active = formatted.filter((t) => ["OPEN", "IN_PROGRESS"].includes(t.statusNormalized));
    const closed = formatted.filter((t) => ["CLOSED", "RESOLVED"].includes(t.statusNormalized));
    const other = formatted.filter((t) => !["OPEN", "IN_PROGRESS", "CLOSED", "RESOLVED"].includes(t.statusNormalized));

    return { active, closed, other };
  }, [report, siteName]);

  const startStats = React.useMemo(() => {
    const starts = (report?.inverters || [])
      .map((inv: any) => inv.start_minutes ?? timeToMinutes(inv.start_time_local, null))
      .filter((v: any) => Number.isFinite(v)) as number[];
    if (!starts.length) return { median: null };
    const sorted = [...starts].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    const median = sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
    return { median };
  }, [report]);

  const hourlyPerformanceOptions = React.useMemo(() => {
    const options = report?.power_plant_hourly_performance?.array_options || [];
    return [
      { value: "all", label: "Show all" },
      ...options.map((option: any) => ({
        value: String(option.array_id),
        label: option.label || option.array_name || option.transformer_name || `Array ${option.array_id}`,
      })),
    ];
  }, [report]);

  React.useEffect(() => {
    if (!hourlyPerformanceOptions.some((option) => option.value === hourlyPerformanceFilter)) {
      setHourlyPerformanceFilter("all");
    }
  }, [hourlyPerformanceFilter, hourlyPerformanceOptions]);

  const hourlyPerformanceRows = React.useMemo(() => {
    const rows = report?.power_plant_hourly_performance?.rows || [];
    if (hourlyPerformanceFilter === "all") {
      return rows.filter((row: any) => row.array_id === null || row.array_id === undefined);
    }
    const targetArrayId = Number(hourlyPerformanceFilter);
    if (!Number.isFinite(targetArrayId)) return [];
    return rows.filter((row: any) => Number(row.array_id) === targetArrayId);
  }, [hourlyPerformanceFilter, report]);

  const productionData = React.useMemo(() => {
    const chart = report?.charts?.production_vs_irradiation || {};
    const x: string[] = chart.x || [];
    const real: number[] = chart.energy_real_kwh || [];
    const theoretical: number[] = chart.energy_theoretical_kwh || [];
    const irrad: Array<number | null> = chart.irradiation_mean_wm2 || [];
    const ENERGY_EPS = 0.05; // kWh threshold to treat as zero
    const clean = (val: any, fallback = 0) => {
      const num = Number(val);
      return Number.isFinite(num) ? num : fallback;
    };

    const rows = x.map((label: string, idx: number) => ({
      label,
      real: clean(real[idx], 0),
      theoretical: clean(theoretical[idx], 0),
      irrad: irrad[idx] !== undefined && irrad[idx] !== null ? clean(irrad[idx], null) : null,
    }));

    const nonZero = rows.filter((r) => (r.real || 0) > ENERGY_EPS || (r.theoretical || 0) > ENERGY_EPS);
    const plotRows = nonZero.length ? nonZero : rows;

    const realPlot = plotRows.map((r) => r.real || 0);
    const theoreticalPlot = plotRows.map((r) => r.theoretical || 0);
    const irradPlot = plotRows.map((r) => (r.irrad !== null ? r.irrad : null));
    const categories = plotRows.map((r) => r.label);
    const maxEnergy = Math.max(...realPlot, ...theoreticalPlot, 0);

    return { categories, realPlot, theoreticalPlot, irradPlot, maxEnergy };
  }, [report]);

  const productionOption = React.useMemo(() => {
    if (!productionData.categories.length) return null;
    const numberFmt = (val: number, fraction = 1) => Number(val || 0).toFixed(fraction);
    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        valueFormatter: (val: number, series: any) => {
          if (series?.seriesName === "Irradiation") return `${numberFmt(val, 0)} W/m²`;
          return `${numberFmt(val)} kWh`;
        },
      },
      legend: {
        data: ["Real", "Theoretical", "Irradiation"],
        bottom: 0,
      },
      grid: { left: 60, right: 60, bottom: 50, top: 20, containLabel: true },
      xAxis: {
        type: "category",
        data: productionData.categories,
        axisLabel: { rotate: 0 },
      },
      yAxis: [
        {
          type: "value",
          name: "kWh",
          position: "left",
          axisLabel: { formatter: (val: number) => `${numberFmt(val)} kWh` },
        },
        {
          type: "value",
          name: "Irradiation (W/m²)",
          position: "right",
          axisLabel: { formatter: (val: number) => `${numberFmt(val, 0)} W/m²` },
        },
      ],
      series: [
        {
          name: "Real",
          type: "bar",
          data: productionData.realPlot,
          itemStyle: { color: "#FFB74D" },
          barGap: "15%",
        },
        {
          name: "Theoretical",
          type: "bar",
          data: productionData.theoreticalPlot,
          itemStyle: { color: "#4FC3F7" },
          barGap: "15%",
        },
        {
          name: "Irradiation",
          type: "line",
          data: productionData.irradPlot,
          yAxisIndex: 1,
          smooth: true,
          lineStyle: { color: "#0ea5e9", width: 2 },
          itemStyle: { color: "#0ea5e9" },
        },
      ],
    };
  }, [productionData]);

  const transformerData = React.useMemo(() => {
    const items = report?.transformers || [];
    if (!items.length) return null;
    const categories = items.map((t: any) => t.name || "");
    const energy = items.map((t: any) => Number(t.energy_mwh) || 0);
    const ratio = items.map((t: any) =>
      t.energy_expected_ratio === null || t.energy_expected_ratio === undefined ? null : Number(t.energy_expected_ratio)
    );
    const maxRatio = Math.max(...ratio.filter((v: number | null) => v !== null).map(Number), 0);
    return { categories, energy, ratio, maxRatio };
  }, [report]);

  const transformerOption = React.useMemo(() => {
    if (!transformerData) return null;
    const ratioAxisMax = Math.max(120, Math.ceil((transformerData.maxRatio || 0) / 10) * 10);
    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params: any[]) => {
          const energyPoint = params.find((p) => p.seriesName === "Energy (MWh)");
          const ratioPoint = params.find((p) => p.seriesName === "Energy / Expected %");
          const name = energyPoint?.name || ratioPoint?.name || "";
          const parts = [];
          if (energyPoint) parts.push(`Energy: ${Number(energyPoint.value || 0).toFixed(2)} MWh`);
          if (ratioPoint && ratioPoint.value !== null && ratioPoint.value !== undefined)
            parts.push(`Energy / Expected: ${Number(ratioPoint.value || 0).toFixed(1)}%`);
          return [`<strong>${name}</strong>`, ...parts].join("<br/>");
        },
      },
      legend: { data: ["Energy (MWh)", "Energy / Expected %"], bottom: 0 },
      grid: { top: 30, bottom: 90, left: 60, right: 70, containLabel: true },
      dataZoom: [
        { type: "inside", xAxisIndex: 0 },
        { type: "slider", xAxisIndex: 0, height: 16, bottom: 38 },
      ],
      xAxis: {
        type: "category",
        data: transformerData.categories,
        axisLabel: { rotate: 45, margin: 12 },
      },
      yAxis: [
        {
          type: "value",
          name: "MWh",
          position: "left",
          axisLabel: { formatter: (val: number) => `${val.toFixed(1)} MWh` },
          min: 0,
        },
        {
          type: "value",
          name: "Energy / Expected %",
          position: "right",
          axisLabel: { formatter: (val: number) => `${val}%` },
          min: 0,
          max: ratioAxisMax,
        },
      ],
      series: [
        {
          name: "Energy (MWh)",
          type: "bar",
          data: transformerData.energy,
          yAxisIndex: 0,
          itemStyle: { color: "#1d4ed8" },
          barWidth: 24,
          barGap: "30%",
          label: {
            show: true,
            position: "insideBottom",
            rotate: 90,
            color: "#e2e8f0",
            fontSize: 10,
            formatter: (p: any) => p.name || "",
          },
        },
        {
          name: "Energy / Expected %",
          type: "bar",
          data: transformerData.ratio,
          yAxisIndex: 1,
          itemStyle: { color: "#22c55e" },
          barWidth: 24,
          barGap: "30%",
          label: {
            show: true,
            position: "top",
            formatter: (p: any) => (p.value === null || p.value === undefined ? "" : `${Number(p.value || 0).toFixed(0)}%`),
          },
        },
      ],
    };
  }, [transformerData]);

  const temperatureData = React.useMemo(() => {
    const chart = report?.charts?.temperatures || {};
    const x: string[] = chart.x || [];
    const air: Array<number | null> = chart.air_temp_median_c || [];
    const moduleTemps: Array<number | null> = chart.module_temp_median_c || [];
    const countsMatch = x.length && air.length === x.length && moduleTemps.length === x.length;
    if (!countsMatch) return null;
    return { x, air, moduleTemps };
  }, [report]);

  const temperatureOption = React.useMemo(() => {
    if (!temperatureData) return null;
    return {
      tooltip: { trigger: "axis" },
      legend: { data: ["Air Temp", "Module Temp"], bottom: 0 },
      grid: { left: 55, right: 55, bottom: 50, top: 20, containLabel: true },
      xAxis: { type: "category", data: temperatureData.x, axisLabel: { rotate: 0 } },
      yAxis: { type: "value", name: "°C" },
      series: [
        { name: "Air Temp", type: "line", data: temperatureData.air, smooth: true, lineStyle: { width: 2, color: "#0ea5e9" } },
        { name: "Module Temp", type: "line", data: temperatureData.moduleTemps, smooth: true, lineStyle: { width: 2, color: "#10b981" } },
      ],
    };
  }, [temperatureData]);

  const energySetpointData = React.useMemo(() => {
    const chart = report?.charts?.energy_vs_setpoint || {};
    const x: string[] = chart.x || [];
    const powerMw: Array<number | null> = chart.site_power_mw || [];
    const setpoint: Array<number | null> = chart.setpoint_percent || [];
    const countsMatch = x.length && powerMw.length === x.length && setpoint.length === x.length;
    if (!countsMatch) return null;
    return { x, powerMw, setpoint };
  }, [report]);

  const energySetpointOption = React.useMemo(() => {
    if (!energySetpointData) return null;
    const maxSetpoint = Math.max(...energySetpointData.setpoint.filter((v) => v !== null && v !== undefined).map(Number), 0);
    const setpointAxisMax = Math.max(110, Math.ceil(maxSetpoint / 10) * 10 || 0);
    return {
      tooltip: {
        trigger: "axis",
        valueFormatter: (val: number, series: any) => {
          if (series?.seriesName === "Setpoint") return `${Number(val || 0).toFixed(1)} %`;
          return `${Number(val || 0).toFixed(2)} MW`;
        },
      },
      legend: { data: ["POI Power", "Setpoint"], bottom: 0 },
      grid: { left: 60, right: 60, bottom: 70, top: 20, containLabel: true },
      xAxis: { type: "category", data: energySetpointData.x },
      yAxis: [
        { type: "value", name: "MW", position: "left", axisLabel: { formatter: (val: number) => `${Number(val || 0).toFixed(1)} MW` } },
        { type: "value", name: "Setpoint %", position: "right", min: 0, max: setpointAxisMax, axisLabel: { formatter: (val: number) => `${val}%` } },
      ],
      dataZoom: [
        { type: "inside", xAxisIndex: 0 },
        { type: "slider", xAxisIndex: 0, height: 16, bottom: 28 },
      ],
      series: [
        { name: "POI Power", type: "line", data: energySetpointData.powerMw, smooth: true, yAxisIndex: 0, lineStyle: { width: 2, color: "#0ea5e9" } },
        { name: "Setpoint", type: "line", data: energySetpointData.setpoint, smooth: true, yAxisIndex: 1, lineStyle: { width: 2, color: "#22c55e" } },
      ],
    };
  }, [energySetpointData]);

  const sortedInverters = React.useMemo(() => {
    const rows = (report?.inverters || []).map((inv: any) => {
      const energyYield = inv.energy_yield_kwh ?? inv.energy_real_kwh ?? inv.energy_yield ?? null;
      const energyExpected = inv.energy_expected_kwh ?? inv.energy_expected ?? null;
      const ratio = energyExpected ? (energyYield / energyExpected) * 100 : null;
      return {
        ...inv,
        energyYield,
        energyExpected,
        ratio,
        startMinutes: inv.start_minutes ?? timeToMinutes(inv.start_time_local, null),
        stopMinutes: inv.stop_minutes ?? timeToMinutes(inv.stop_time_local, null),
      };
    });
    const dir = inverterSort.dir === "asc" ? 1 : -1;
    const valueFor = (inv: any) => {
      switch (inverterSort.key) {
        case "pr":
          return inv.pr ?? null;
        case "availability":
          return inv.availability ?? inv.availability_pct ?? null;
        case "energy":
          return inv.energyYield ?? null;
        case "ratio":
          return inv.ratio ?? null;
        case "start":
          return inv.startMinutes ?? null;
        case "stop":
          return inv.stopMinutes ?? null;
        default:
          return (inv.inverter || "") as string;
      }
    };
    rows.sort((a, b) => {
      const va = valueFor(a);
      const vb = valueFor(b);
      // string compare for inverter name
      if (typeof va === "string" || typeof vb === "string") {
        return dir * String(va || "").localeCompare(String(vb || ""), undefined, { sensitivity: "base" });
      }
      const na = va ?? (inverterSort.dir === "asc" ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY);
      const nb = vb ?? (inverterSort.dir === "asc" ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY);
      return na === nb ? 0 : na > nb ? dir : -dir;
    });
    return rows;
  }, [report, inverterSort]);

  const toggleInverterSort = (key: typeof inverterSort.key) => {
    setInverterSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      }
      return { key, dir: "asc" };
    });
  };

  const exportInvertersExcel = () => {
    const rows = sortedInverters;
    if (!rows.length) return;
    const safe = (val: any) => (val === null || val === undefined ? "" : String(val));
    const td = (val: any, fmt?: string) =>
      `<td${fmt ? ` style="mso-number-format:'${fmt}'"` : ""}>${safe(val)}</td>`;
    const toPercentVal = (val: number | null | undefined) => (val === null || val === undefined ? null : Number(val) / 100);
    const toExcelTime = (minutes: number | null | undefined) => (minutes === null || minutes === undefined ? null : minutes / 1440);
    const header = ["Inverter", "PR %", "Availability", "Energy kWh", "Energy / Expected", "Start", "Stop"];
    const headerHtml = header.map((h) => `<th>${h}</th>`).join("");
    const bodyHtml = rows
      .map((inv: any) => {
        const prVal = toPercentVal(inv.pr);
        const availVal = toPercentVal(inv.availability ?? inv.availability_pct);
        const ratioVal = toPercentVal(inv.ratio);
        const startVal = toExcelTime(inv.startMinutes);
        const stopVal = toExcelTime(inv.stopMinutes);
        const energyVal = inv.energyYield !== null && inv.energyYield !== undefined ? Number(inv.energyYield) : null;
        return `<tr>
          ${td(inv.inverter)}
          ${td(prVal, "0.00%")}
          ${td(availVal, "0.00%")}
          ${td(energyVal, "0.0")}
          ${td(ratioVal, "0.0%")}
          ${td(startVal, "hh:mm")}
          ${td(stopVal, "hh:mm")}
        </tr>`;
      })
      .join("");
    const html = `<table><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>`;
    const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `inverter_production_${date || "report"}.xls`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportHourlyPerformanceExcel = () => {
    const payload = report?.power_plant_hourly_performance;
    const allRows = payload?.rows || [];
    const arrayOptions = payload?.array_options || [];
    if (!allRows.length) return;

    const safe = (val: any) => (val === null || val === undefined ? "" : String(val));
    const td = (val: any, fmt?: string, extraStyle = "") =>
      `<td${fmt || extraStyle ? ` style="${fmt ? `mso-number-format:'${fmt}';` : ""}${extraStyle}"` : ""}>${safe(val)}</td>`;
    const sectionTitle = (label: string) =>
      `<tr><td colspan="7" style="font-weight:bold;background:#f8fafc;border-top:1px solid #cbd5e1;">${safe(label)}</td></tr>`;
    const headerHtml = `<tr>
      <th>Start time</th>
      <th>End time</th>
      <th>Avg DSO setpoint (%)</th>
      <th>Avg irradiation (W/m²)</th>
      <th>Energy real (kWh)</th>
      <th>Energy expected (kWh)</th>
      <th>P.R. ratio</th>
    </tr>`;
    const buildBody = (rows: any[]) =>
      rows
        .map(
          (row: any) => `<tr>
            ${td(row.start_time)}
            ${td(row.end_time)}
            ${td(row.dso_setpoint_percent, "#,##0.00")}
            ${td(row.avg_irradiation_wm2, "#,##0.00")}
            ${td(row.energy_real_kwh, "#,##0")}
            ${td(row.energy_expected_kwh, "#,##0")}
            ${td(row.pr_percent === null || row.pr_percent === undefined ? "" : Number(row.pr_percent) / 100, "0.00%")}
          </tr>`
        )
        .join("");

    const sections = [
      {
        label: "Show all",
        rows: allRows.filter((row: any) => row.array_id === null || row.array_id === undefined),
      },
      ...arrayOptions.map((option: any) => ({
        label: option.label || option.array_name || option.transformer_name || `Array ${option.array_id}`,
        rows: allRows.filter((row: any) => Number(row.array_id) === Number(option.array_id)),
      })),
    ].filter((section) => section.rows.length);

    const html = `<table>
      <tbody>
        ${sections
          .map(
            (section) => `
              ${sectionTitle(section.label)}
              ${headerHtml}
              ${buildBody(section.rows)}
              <tr><td colspan="6"></td></tr>
            `
          )
          .join("")}
      </tbody>
    </table>`;

    const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const fileSiteName = String(siteName || "site").replace(/[\\/:*?\"<>|]/g, "_").trim();
    a.download = `${fileSiteName}_power_plant_hourly_performance_${date || "report"}.xls`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const renderChart = () => {
    if (chartKey === "production") {
      const countsMatch =
        productionData.categories.length > 0 &&
        productionData.realPlot.length === productionData.categories.length &&
        productionData.theoreticalPlot.length === productionData.categories.length &&
        productionData.irradPlot.length === productionData.categories.length;
      if (!countsMatch || productionData.maxEnergy <= 0 || !productionOption) {
        return <div className="text-sm text-slate-500">No hourly production data available for this day.</div>;
      }
      return <ReactECharts option={productionOption as any} style={{ height: 420 }} notMerge />;
    }
    if (chartKey === "transformers") {
      if (!transformerOption) return <div className="text-sm text-slate-500">No transformer data available.</div>;
      return <ReactECharts option={transformerOption as any} style={{ height: 420 }} notMerge />;
    }
    if (chartKey === "temperatures") {
      if (!temperatureOption) return <div className="text-sm text-slate-500">No temperature data available.</div>;
      return <ReactECharts option={temperatureOption as any} style={{ height: 420 }} notMerge />;
    }
    if (chartKey === "setpoint") {
      if (!energySetpointOption) return <div className="text-sm text-slate-500">No setpoint data available.</div>;
      return <ReactECharts option={energySetpointOption as any} style={{ height: 420 }} notMerge />;
    }
    return null;
  };

  const handleDownloadPdf = async () => {
    if (!report) {
      setError("Load a report before downloading PDF.");
      return;
    }
    setPdfGenerating(true);
    try {
      const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const dateLabel = date ? new Date(date).toLocaleDateString() : new Date().toLocaleDateString();
      const siteName = meta.site_name || (meta as any).siteName || "Site";
      const siteLocation = meta.location || {};
      const siteCity = siteLocation.city || meta.city || "";
      const siteCountry = siteLocation.country || meta.country || "";
      const siteAddress = siteLocation.address || meta.address || "";
      const coords =
        meta.gps_latitude && meta.gps_longitude ? `${meta.gps_latitude.toFixed(4)}, ${meta.gps_longitude.toFixed(4)}` : "—";
      const lastTableY = () => (doc as any).lastAutoTable?.finalY as number | undefined;

      const headerBar = (title: string, includeDate = false) => {
        doc.setFillColor(PDF_BLUE[0], PDF_BLUE[1], PDF_BLUE[2]);
        const y = PAGE_MARGIN_PT;
        doc.rect(PAGE_MARGIN_PT, y, pageWidth - PAGE_MARGIN_PT * 2, 24, "F");
        doc.setFontSize(12);
        doc.setTextColor(255, 255, 255);
        doc.text(title, PAGE_MARGIN_PT + 6, y + 16);
        if (includeDate) {
          doc.text(`Report date: ${dateLabel}`, pageWidth - PAGE_MARGIN_PT - 6, y + 16, { align: "right" });
        }
        return y + 32;
      };

      const subHeaderBar = (title: string, startY: number) => {
        doc.setFillColor(PDF_BLUE[0], PDF_BLUE[1], PDF_BLUE[2]);
        doc.rect(PAGE_MARGIN_PT, startY, pageWidth - PAGE_MARGIN_PT * 2, 18, "F");
        doc.setFontSize(11);
        doc.setTextColor(255, 255, 255);
        doc.text(title, PAGE_MARGIN_PT + 6, startY + 13);
        return startY + 28;
      };

      const addFooters = () => {
        const total = doc.getNumberOfPages();
        for (let i = 1; i <= total; i += 1) {
          doc.setPage(i);
          doc.setFontSize(9);
          doc.setTextColor(33, 37, 41);
          doc.text("created with PV-report.com", PAGE_MARGIN_PT, pageHeight - 16);
          doc.text(`page ${i}/${total}`, pageWidth / 2, pageHeight - 16, { align: "center" });
          doc.text("powered by T-NRG", pageWidth - PAGE_MARGIN_PT, pageHeight - 16, { align: "right" });
        }
      };

      const siteInfoRows = [
        ["Site", siteName],
        ["Location", [siteCity, siteCountry].filter(Boolean).join(", ") || "—"],
        ["Address", siteAddress || "—"],
        ["Time zone", meta.timezone || meta.timezoneName || "—"],
        ["Coordinates", coords],
        ["AC capacity (MW)", meta.nominal_ac_mw ? Number(meta.nominal_ac_mw).toFixed(3) : "—"],
        ["DC capacity (MWp)", meta.nominal_dc_mwp ? Number(meta.nominal_dc_mwp).toFixed(3) : "—"],
      ];

      const kpiRows = [
        ["Theoretical energy yield", kpis.energy_theoretical_mwh ? `${kpis.energy_theoretical_mwh.toFixed(2)} MWh` : "—"],
        ["Real energy yield (inverter)", kpis.energy_real_inverter_mwh ? `${kpis.energy_real_inverter_mwh.toFixed(2)} MWh` : "—"],
        ["Real energy yield (POI)", kpis.energy_real_poi_mwh ? `${kpis.energy_real_poi_mwh.toFixed(2)} MWh` : "—"],
        [
          "Losses",
          kpis.losses_mwh && kpis.losses_percent != null
            ? `${kpis.losses_mwh.toFixed(2)} MWh (${kpis.losses_percent.toFixed(2)}%)`
            : "—",
        ],
        ["Performance Ratio Site", percentLabel(kpis.pr_site_percent, 2)],
        ["Inverter availability", percentLabel(kpis.availability_site_percent, 2)],
      ];

      // Cover page
      doc.setFillColor(PDF_BLUE[0], PDF_BLUE[1], PDF_BLUE[2]);
      doc.rect(PAGE_MARGIN_PT, PAGE_MARGIN_PT, 10, pageHeight - PAGE_MARGIN_PT * 2, "F");
      doc.setFontSize(12);
      doc.setTextColor(33, 37, 41);
      doc.text(dateLabel, pageWidth - PAGE_MARGIN_PT, PAGE_MARGIN_PT + 12, { align: "right" });
      doc.setFontSize(32);
      doc.setFont("helvetica", "bold");
      doc.text("DAILY PERFORMANCE REPORT", pageWidth / 2, pageHeight / 2 - 10, { align: "center" });
      doc.setFontSize(20);
      doc.text(siteName, pageWidth / 2, pageHeight / 2 + 20, { align: "center" });
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text([siteAddress, [siteCity, siteCountry].filter(Boolean).join(", ")].filter(Boolean).join(" • ") || "", pageWidth / 2, pageHeight / 2 + 42, {
        align: "center",
      });

      // Page 2: KPIs + charts
      doc.addPage();
      let y = headerBar("KEY PERFORMANCE INDICATORS", true);
      const colWidth = (pageWidth - PAGE_MARGIN_PT * 2 - 12) / 2;
      const cardPad = 8;
      const lineH = 12;

      const ensureSpace = (height: number, title = "KEY PERFORMANCE INDICATORS (cont.)") => {
        if (y + height + PAGE_MARGIN_PT > pageHeight) {
          doc.addPage();
          y = headerBar(title, true);
        }
      };

      const drawCardAt = (
        x: number,
        yStart: number,
        width: number,
        rows: Array<{ label: string; value: string; color?: [number, number, number] }>,
        forcedHeight?: number
      ) => {
        const height = forcedHeight ?? cardPad * 2 + rows.length * lineH;
        doc.setDrawColor(220, 226, 239);
        doc.setFillColor(248, 250, 252);
        doc.rect(x, yStart, width, height, "FD");
        doc.setFontSize(9.5);
        rows.forEach((row, idx) => {
          const baseY = yStart + cardPad + lineH * (idx + 0.65);
          doc.setTextColor(71, 85, 105);
          doc.setFont("helvetica", "bold");
          doc.text(`${row.label}:`, x + cardPad, baseY);
          doc.setFont("helvetica", "normal");
          if (row.color) {
            doc.setTextColor(row.color[0], row.color[1], row.color[2]);
          } else {
            doc.setTextColor(33, 37, 41);
          }
          doc.text(row.value || "—", x + width - cardPad, baseY, { align: "right" });
        });
        return yStart + height;
      };

      const leftRows = siteInfoRows.map(([label, value]) => ({ label, value }));
      const rightRows = kpiRows.map(([label, value]) => {
        const color =
          label === "Performance Ratio Site"
            ? colorForPr(typeof kpis.pr_site_percent === "number" ? Number(kpis.pr_site_percent) : null)
            : label === "Inverter availability"
              ? colorForAvailability(typeof kpis.availability_site_percent === "number" ? Number(kpis.availability_site_percent) : null)
              : undefined;
        return { label, value: String(value), color };
      });

      const leftHeight = cardPad * 2 + leftRows.length * lineH;
      const rightHeight = cardPad * 2 + rightRows.length * lineH;
      const cardHeight = Math.max(leftHeight, rightHeight);
      ensureSpace(cardHeight + 12);
      const cardStartY = y;
      const leftEnd = drawCardAt(PAGE_MARGIN_PT, cardStartY, colWidth, leftRows, cardHeight);
      const rightEnd = drawCardAt(PAGE_MARGIN_PT + colWidth + 12, cardStartY, colWidth, rightRows, cardHeight);
      y = Math.max(leftEnd, rightEnd) + 12;

      // Summary mini-cards row
      const smallCardWidth = (pageWidth - PAGE_MARGIN_PT * 2 - 16) / 3;
      const miniRows: Array<Array<{ label: string; value: string; color?: [number, number, number] }>> = [
        [
          { label: "Warnings", value: String(kpis.warnings ?? "—"), color: ORANGE },
          { label: "Critical", value: String(kpis.critical ?? "—"), color: RED },
          { label: "Tickets", value: String(kpis.tickets ?? "—"), color: SLATE },
        ],
        [
          { label: "Sunrise", value: meta.sunrise || "—" },
          { label: "Sunset", value: meta.sunset || "—" },
          { label: "Sunny hours", value: meta.sunny_hours != null ? `${Number(meta.sunny_hours).toFixed(2)} h` : "—" },
        ],
        [
          { label: "Irradiation Max (W/m²)", value: meta.irradiation_max_wm2 != null ? `${Number(meta.irradiation_max_wm2).toFixed(0)}` : "—" },
          { label: "Irradiation Avg (W/m²)", value: meta.irradiation_mean_wm2 != null ? `${Number(meta.irradiation_mean_wm2).toFixed(0)}` : "—" },
        ],
      ];

      const maxMiniRows = Math.max(...miniRows.map((r) => r.length));
      miniRows.forEach((rows) => {
        while (rows.length < maxMiniRows) {
          rows.push({ label: "", value: "" });
        }
      });

      const drawMini = (
        x: number,
        yStart: number,
        rows: Array<{ label: string; value: string; color?: [number, number, number] }>
      ) => {
        const height = cardPad * 2 + rows.length * lineH;
        doc.setDrawColor(220, 226, 239);
        doc.setFillColor(248, 250, 252);
        doc.rect(x, yStart, smallCardWidth, height, "FD");
        doc.setFontSize(9.5);
        rows.forEach((row, idx) => {
          const baseY = yStart + cardPad + lineH * (idx + 0.65);
          doc.setTextColor(71, 85, 105);
          doc.setFont("helvetica", "bold");
          doc.text(`${row.label}:`, x + cardPad, baseY);
          doc.setFont("helvetica", "normal");
          if (row.color) {
            doc.setTextColor(row.color[0], row.color[1], row.color[2]);
          } else {
            doc.setTextColor(33, 37, 41);
          }
          doc.text(row.value || "—", x + smallCardWidth - cardPad, baseY, { align: "right" });
        });
        return yStart + height;
      };

      const miniHeight = cardPad * 2 + maxMiniRows * lineH;
      ensureSpace(miniHeight + 12);
      const miniStartY = y + 6;
      const end1 = drawMini(PAGE_MARGIN_PT, miniStartY, miniRows[0]);
      const end2 = drawMini(PAGE_MARGIN_PT + smallCardWidth + 8, miniStartY, miniRows[1]);
      const end3 = drawMini(PAGE_MARGIN_PT + (smallCardWidth + 8) * 2, miniStartY, miniRows[2]);
      y = Math.max(end1, end2, end3) + 12;

      const prodImg = await chartToDataUrl(productionOption);
      if (prodImg) {
        const imgWidth = pageWidth - PAGE_MARGIN_PT * 2;
        const imgHeight = (imgWidth * 420) / 900;
        if (y + imgHeight + 40 > pageHeight) {
          doc.addPage();
          y = headerBar("KEY PERFORMANCE INDICATORS (cont.)", true);
        }
        y = subHeaderBar("ENERGY GENERATION AND AVERAGE IRRADIATION PER HOUR", y);
        doc.addImage(prodImg, "PNG", PAGE_MARGIN_PT, y, imgWidth, imgHeight);
        y += imgHeight + 14;
      }

      const setpointImg = await chartToDataUrl(energySetpointOption);
      if (setpointImg) {
        const imgWidth = pageWidth - PAGE_MARGIN_PT * 2;
        const imgHeight = (imgWidth * 420) / 900;
        if (y + imgHeight + 40 > pageHeight) {
          doc.addPage();
          y = headerBar("KEY PERFORMANCE INDICATORS (cont.)", true);
        }
        y = subHeaderBar("OUTPUT POWER AND DSO SETPOINT", y);
        doc.addImage(setpointImg, "PNG", PAGE_MARGIN_PT, y, imgWidth, imgHeight);
      }

      // Page 3: Transformers
      doc.addPage();
      y = headerBar("PERFORMANCE PER TRANSFORMER", true);
      const transformerRows = (report?.transformers || [])
        .slice()
        .sort((a: any, b: any) => String(a.name || a.transformer || "").localeCompare(String(b.name || b.transformer || ""), undefined, { sensitivity: "base" }))
        .map((t: any) => [
          t.name || t.transformer || "Transformer",
          numberLabel(t.energy_mwh, 2, " MWh"),
          percentLabel(t.energy_expected_ratio, 1),
          percentLabel(t.pr, 2),
          percentLabel(t.availability, 2),
          t,
        ]);
      autoTable(doc, {
        startY: y,
        head: [["Transformer", "Energy (MWh)", "Energy / Expected", "PR %", "Availability %"]],
        body: transformerRows,
        theme: "grid",
        styles: { fontSize: 9 },
        headStyles: { fillColor: PDF_BLUE, textColor: [255, 255, 255], halign: "center" },
        columnStyles: {
          0: { halign: "center" },
          1: { halign: "right" },
          2: { halign: "right" },
          3: { halign: "right" },
          4: { halign: "right" },
        },
        didParseCell: (data) => {
          if (data.section === "body") {
            const rowData = data.row.raw as any[];
            const original = rowData[5] as any;
            if (data.column.index === 2) {
              const val = typeof original?.energy_expected_ratio === "number" ? original.energy_expected_ratio : null;
              const color = val !== null ? (val >= 90 ? GREEN : val >= 75 ? ORANGE : RED) : SLATE;
              data.cell.styles.textColor = color;
            }
            if (data.column.index === 3) {
              const val = typeof original?.pr === "number" ? original.pr : null;
              const color = colorForPr(val);
              data.cell.styles.textColor = color;
            }
            if (data.column.index === 4) {
              const val = typeof original?.availability === "number" ? original.availability : null;
              const color = colorForAvailability(val);
              data.cell.styles.textColor = color;
            }
          }
        },
      });
      {
        const lastY = lastTableY();
        y = lastY ? lastY + 16 : y + 16;
      }
      const transformerImg = await chartToDataUrl(transformerOption);
      if (transformerImg) {
        const imgWidth = pageWidth - PAGE_MARGIN_PT * 2;
        const imgHeight = (imgWidth * 420) / 900;
        if (y + imgHeight + 40 > pageHeight) {
          doc.addPage();
          y = headerBar("PERFORMANCE PER TRANSFORMER (cont.)", true);
        }
        y = subHeaderBar("ENERGY PRODUCTION PER TRANSFORMER", y);
        doc.addImage(transformerImg, "PNG", PAGE_MARGIN_PT, y, imgWidth, imgHeight);
      }

      // Page 4: Inverters
      doc.addPage();
      y = headerBar("PERFORMANCE PER INVERTER", true);
      const inverterRows = sortedInverters
        .slice()
        .sort((a: any, b: any) => String(a.inverter || "").localeCompare(String(b.inverter || ""), undefined, { sensitivity: "base" }))
        .map((inv: any) => [
          inv.inverter || "—",
          percentLabel(inv.pr, 2),
          percentLabel(inv.availability ?? inv.availability_pct, 2),
          inv.energyYield !== null && inv.energyYield !== undefined ? Number(inv.energyYield).toFixed(1) : "—",
          percentLabel(inv.ratio, 1),
          inv.start_time_local || "—",
          inv.stop_time_local || "—",
          inv,
        ]);
      autoTable(doc, {
        startY: y,
        head: [["Inverter", "PR %", "Availability", "Energy kWh", "Energy / Expected", "Start", "Stop"]],
        body: inverterRows,
        theme: "grid",
        styles: { fontSize: 8.5, halign: "center" },
        headStyles: { fillColor: PDF_BLUE, textColor: [255, 255, 255], halign: "center" },
        columnStyles: {
          0: { halign: "center" },
          1: { halign: "center" },
          2: { halign: "center" },
          3: { halign: "center" },
          4: { halign: "center" },
          5: { halign: "center" },
          6: { halign: "center" },
        },
        didParseCell: (data) => {
          if (data.section === "body") {
            const rowData = data.row.raw as any[];
            const original = rowData[7] as any;
            if (data.column.index === 1) {
              const val = typeof original?.pr === "number" ? original.pr : null;
              data.cell.styles.textColor = colorForPr(val);
            }
            if (data.column.index === 2) {
              const val = typeof original?.availability === "number" ? original.availability : original?.availability_pct ?? null;
              data.cell.styles.textColor = colorForAvailability(val);
            }
            if (data.column.index === 4) {
              const val = typeof original?.ratio === "number" ? original.ratio : null;
              const color = val !== null ? (val >= 90 ? GREEN : val >= 75 ? ORANGE : RED) : SLATE;
              data.cell.styles.textColor = color;
            }
            if (data.column.index === 5) {
              const median = startStats.median;
              const startMin = original?.startMinutes ?? null;
              if (median != null && startMin != null) {
                const delta = startMin - median;
                if (delta > 20) data.cell.styles.textColor = RED;
                else if (delta > 10) data.cell.styles.textColor = ORANGE;
              }
            }
          }
        },
      });

      // Page 5: Maintenance tickets
      doc.addPage();
      y = headerBar("MAINTENANCE TICKETS", true);
      const ticketTable = (title: string, rows: ReportTicketDisplay[]) => {
        doc.setFontSize(11);
        doc.setTextColor(33, 37, 41);
        doc.text(title.toUpperCase(), PAGE_MARGIN_PT, y + 8);
        y += 14;
        const sortedRows = rows
          .slice()
          .sort((a, b) => Number(a.id) - Number(b.id));
        autoTable(doc, {
          startY: y,
          head: [["Ticket", "Transformer / Inverter", "Opened / Closed", "Status", "Severity", "Message"]],
          body: sortedRows.map((t) => [
            `#${t.id}`,
            `${t.transformerLabel || "—"}\n${t.inverterLabel || "—"}`,
            `${t.openedLabel || "—"}\n${t.closedLabel || "—"}`,
            t.statusLabel,
            t.severityLabel || "—",
            t.message || "",
          ]),
          theme: "grid",
          styles: { fontSize: 8.5, cellWidth: "wrap", valign: "middle" },
          columnStyles: { 1: { cellWidth: 95 }, 2: { cellWidth: 90 }, 5: { cellWidth: 160 } },
          headStyles: { fillColor: PDF_BLUE, textColor: [255, 255, 255] },
        });
        const lastY = lastTableY();
        y = lastY ? lastY + 12 : y + 12;
      };
      ticketTable("Active Tickets", ticketGroups.active);
      ticketTable("Closed Tickets", ticketGroups.closed);
      if (ticketGroups.other.length) {
        ticketTable("Other Tickets", ticketGroups.other);
      }

      // Page 6: Weather + temperature chart
      doc.addPage();
      y = headerBar("HOURLY WEATHER DATA", true);
      const weatherRows = (report?.weather || []).map((w: any) => [
        w.time || "—",
        w.temp_c ?? "—",
        w.clouds ?? "—",
        w.wind_speed ?? "—",
        w.description || "—",
      ]);
      autoTable(doc, {
        startY: y,
        head: [["Time", "Temp (°C)", "Clouds %", "Wind m/s", "Description"]],
        body: weatherRows,
        theme: "grid",
        styles: { fontSize: 9, halign: "center" },
        headStyles: { fillColor: PDF_BLUE, textColor: [255, 255, 255], halign: "center" },
        columnStyles: {
          4: { halign: "center" },
        },
      });
      {
        const lastY = lastTableY();
        y = lastY ? lastY + 16 : y + 16;
      }
      const tempImg = await chartToDataUrl(temperatureOption);
      if (tempImg) {
        const imgWidth = pageWidth - PAGE_MARGIN_PT * 2;
        const imgHeight = (imgWidth * 420) / 900;
        if (y + imgHeight + 40 > pageHeight) {
          doc.addPage();
          y = headerBar("HOURLY WEATHER DATA (cont.)", true);
        }
        y = subHeaderBar("AIR AND PV MODULE TEMPERATURES", y);
        doc.addImage(tempImg, "PNG", PAGE_MARGIN_PT, y, imgWidth, imgHeight);
      }

      addFooters();
      const safeSite = siteName ? siteName.replace(/\\s+/g, "_").replace(/[^a-zA-Z0-9_-]/g, "") : "site";
      doc.save(`report_${safeSite}_${dateLabel}.pdf`);
    } catch (err) {
      console.error("PDF generation failed", err);
      setError("Failed to generate PDF.");
    } finally {
      setPdfGenerating(false);
    }
  };

  return (
    <main className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Reports</h1>
          <p className="text-slate-600">Stored daily reports with PDF-ready data.</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleDownloadPdf}
            disabled={pdfGenerating || !report}
            className="rounded bg-slate-800 px-4 py-2 text-white shadow hover:bg-slate-700 disabled:opacity-50"
          >
            {pdfGenerating ? "Building PDF..." : "Download PDF"}
          </button>
          <button
            onClick={handleRegenerate}
            disabled={regenerating || !selectedSite}
            className="rounded bg-blue-600 px-4 py-2 text-white shadow hover:bg-blue-500 disabled:opacity-50"
          >
            {regenerating ? "Regenerating..." : "Regenerate (admin)"}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 rounded-2xl border bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-1">
          <label className="text-sm text-slate-500">Site</label>
          <select
            value={selectedSite}
            onChange={(e) => setSelectedSite(e.target.value)}
            className="rounded-lg border px-3 py-2 shadow-inner"
          >
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-sm text-slate-500">Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="rounded-lg border px-3 py-2 shadow-inner"
          />
        </div>
        <div className="flex flex-col justify-end">
          <button
            onClick={fetchReport}
            disabled={loading}
            className="rounded-lg bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800 disabled:opacity-50"
          >
            {loading ? "Loading..." : "Load report"}
          </button>
          {status && (
            <span className="text-xs text-slate-500">
              Status: <span className="font-semibold">{status}</span>
            </span>
          )}
        </div>
      </div>

      {error && <div className="rounded border border-red-200 bg-red-50 p-3 text-red-700">{error}</div>}
      {warning && <div className="rounded border border-amber-200 bg-amber-50 p-3 text-amber-800">{warning}</div>}

      {report && (
        <div className="space-y-6">
      <div className="grid gap-5 lg:grid-cols-1 xl:grid-cols-2">
        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wide text-slate-500">Site</p>
            <div className="text-xl font-semibold text-slate-900 leading-6">{meta.site_name}</div>
            <div className="text-xs text-slate-600 leading-5">
              {meta.location?.address || ""} {meta.location?.city || ""} {meta.location?.country || ""}
            </div>
            <div className="flex gap-6 text-sm font-semibold text-slate-700 leading-6">
              <span>Nominal AC: {meta.nominal_ac_mw ?? "—"} MW</span>
              <span>Nominal DC: {meta.nominal_dc_mwp ?? "—"} MWp</span>
            </div>
          </div>

          <div className="mt-4 space-y-3 rounded-xl border border-slate-200 bg-slate-50/80 p-4 shadow-inner">
            <MetricLine label="Theoretical energy yield" value={kpis.energy_theoretical_mwh?.toFixed ? kpis.energy_theoretical_mwh.toFixed(2) : "—"} unit="MWh" />
            <MetricLine label="Real energy yield (inverter)" value={kpis.energy_real_inverter_mwh?.toFixed ? kpis.energy_real_inverter_mwh.toFixed(2) : "—"} unit="MWh" />
            <MetricLine label="Real energy yield (POI)" value={kpis.energy_real_poi_mwh?.toFixed ? kpis.energy_real_poi_mwh.toFixed(2) : "—"} unit="MWh" />
            <MetricLine
              label="Losses"
              value={
                kpis.losses_mwh?.toFixed
                  ? `${kpis.losses_mwh.toFixed(2)} (${kpis.losses_percent?.toFixed ? kpis.losses_percent.toFixed(2) : "—"}%)`
                  : "—"
              }
                unit="MWh"
              />
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Performance Ratio Site:</span>
              <span className={`font-semibold ${prTone(kpis.pr_site_percent)}`}>
                {kpis.pr_site_percent?.toFixed ? kpis.pr_site_percent.toFixed(2) : "—"}%
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Inverter availability:</span>
              <span className={`font-semibold ${ratioTone(kpis.availability_site_percent)}`}>
                {kpis.availability_site_percent?.toFixed ? kpis.availability_site_percent.toFixed(2) : "—"}%
              </span>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm text-slate-800">
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
              <MetricLine label="Warnings" value={kpis.warnings ?? "—"} tone="warn" />
              <MetricLine label="Critical" value={kpis.critical ?? "—"} tone="crit" />
              <MetricLine label="Tickets" value={kpis.tickets ?? "—"} />
            </div>
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
              <MetricLine label="Sunrise" value={meta.sunrise || "—"} />
              <MetricLine label="Sunset" value={meta.sunset || "—"} />
              <MetricLine label="Sunny hours" value={meta.sunny_hours ? Number(meta.sunny_hours).toFixed(2) : "—"} unit="h" />
            </div>
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
              <div className="text-sm font-semibold text-slate-900 mb-1">Irradiation (W/m²)</div>
              <MetricLine label="Maximum" value={meta.irradiation_max_wm2 ? Number(meta.irradiation_max_wm2).toFixed(0) : "—"} />
              <MetricLine label="Average" value={meta.irradiation_mean_wm2 ? Number(meta.irradiation_mean_wm2).toFixed(0) : "—"} />
            </div>
          </div>
        </div>

        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-base font-semibold text-slate-900">Charts</h3>
            <select
              value={chartKey}
              onChange={(e) => setChartKey(e.target.value as any)}
              className="rounded-lg border px-2 py-1 text-sm shadow-inner"
            >
              <option value="production">Production vs Irradiation</option>
              <option value="transformers">Energy by Transformer</option>
              <option value="temperatures">Temperatures</option>
              <option value="setpoint">Energy vs Setpoint</option>
            </select>
          </div>
          <div className="rounded-xl border border-slate-100 bg-slate-50/70 p-3">
            {renderChart()}
          </div>
        </div>
      </div>

          <section className="grid gap-6 lg:grid-cols-2">
            <TableCard title="Best Inverters" rows={report?.rankings?.best_inverters || []} />
            <TableCard title="Worst Inverters" rows={report?.rankings?.worst_inverters || []} />
          </section>

          <ToggleCard title="Transformers" defaultOpen={false}>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {(report?.transformers || [])
                .slice()
                .sort((a: any, b: any) => (a.name || "").localeCompare(b.name || ""))
                .map((t: any, idx: number) => {
                  const pr = t.pr ?? null;
                  const availability = t.availability ?? null;
                  const energyMwh = t.energy_mwh ?? null;
                  const energyExpected = t.energy_expected_ratio ?? null;
                  return (
                    <div key={idx} className="rounded-xl border bg-white p-3 shadow-sm space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold text-slate-900">{t.name || `Transformer ${idx + 1}`}</div>
                        <div className="text-sm font-semibold text-slate-900">
                          Energy: {energyMwh !== null ? `${Number(energyMwh).toFixed(2)} MWh` : "—"}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 items-center">
                        <div className="flex flex-col items-center gap-1">
                          <ReactECharts option={gaugeOption(availability, "availability", 100)} style={{ height: 80, width: "100%" }} notMerge />
                          <div className="text-xs font-semibold text-slate-700">Availability</div>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                          <ReactECharts option={gaugeOption(pr, "pr", 100)} style={{ height: 80, width: "100%" }} notMerge />
                          <div className="text-xs font-semibold text-slate-700">PR</div>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                          <ReactECharts option={gaugeOption(energyExpected, "ratio", 120)} style={{ height: 80, width: "100%" }} notMerge />
                          <div className="text-xs font-semibold text-slate-700">Energy / Expected</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </ToggleCard>

          <ToggleCard
            title="Power plant hourly performance"
            defaultOpen={false}
            action={
              <>
                <select
                  value={hourlyPerformanceFilter}
                  onChange={(e) => setHourlyPerformanceFilter(e.target.value)}
                  className="rounded border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 shadow-sm"
                >
                  {hourlyPerformanceOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <button
                  onClick={exportHourlyPerformanceExcel}
                  className="rounded border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                >
                  Export Excel
                </button>
              </>
            }
          >
            {hourlyPerformanceRows.length ? (
              <div className="overflow-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-3 py-2 text-center">Start time</th>
                      <th className="px-3 py-2 text-center">End time</th>
                      <th className="px-3 py-2 text-right">Avg DSO setpoint (%)</th>
                      <th className="px-3 py-2 text-right">Avg irradiation (W/m²)</th>
                      <th className="px-3 py-2 text-right">Energy real (kWh)</th>
                      <th className="px-3 py-2 text-right">Energy expected (kWh)</th>
                      <th className="px-3 py-2 text-right">P.R. ratio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hourlyPerformanceRows.map((row: any, idx: number) => (
                      <tr key={`${row.array_id ?? "all"}-${row.start_time}-${idx}`} className="border-t">
                        <td className="px-3 py-2 text-center tabular-nums">{row.start_time || "—"}</td>
                        <td className="px-3 py-2 text-center tabular-nums">{row.end_time || "—"}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{groupedNumberLabel(row.dso_setpoint_percent, 2)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{groupedNumberLabel(row.avg_irradiation_wm2, 2)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{groupedNumberLabel(row.energy_real_kwh, 0)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{groupedNumberLabel(row.energy_expected_kwh, 0)}</td>
                        <td className={`px-3 py-2 text-right font-semibold tabular-nums ${prTone(row.pr_percent)}`}>{percentLabel(row.pr_percent, 2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-sm text-slate-500">No hourly performance data available for this selection.</div>
            )}
          </ToggleCard>

          <ToggleCard
            title="Energy production per inverter"
            defaultOpen={false}
            action={
              <button
                onClick={exportInvertersExcel}
                className="rounded border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
              >
                Export Excel
              </button>
            }
          >
            <div className="overflow-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2 cursor-pointer" onClick={() => toggleInverterSort("inverter")}>
                      Inverter {inverterSort.key === "inverter" ? (inverterSort.dir === "asc" ? "▲" : "▼") : ""}
                    </th>
                    <th className="px-3 py-2 cursor-pointer" onClick={() => toggleInverterSort("pr")}>
                      PR % {inverterSort.key === "pr" ? (inverterSort.dir === "asc" ? "▲" : "▼") : ""}
                    </th>
                    <th className="px-3 py-2 cursor-pointer" onClick={() => toggleInverterSort("availability")}>
                      Availability {inverterSort.key === "availability" ? (inverterSort.dir === "asc" ? "▲" : "▼") : ""}
                    </th>
                    <th className="px-3 py-2 cursor-pointer" onClick={() => toggleInverterSort("energy")}>
                      Energy kWh {inverterSort.key === "energy" ? (inverterSort.dir === "asc" ? "▲" : "▼") : ""}
                    </th>
                    <th className="px-3 py-2 cursor-pointer" onClick={() => toggleInverterSort("ratio")}>
                      Energy / Expected {inverterSort.key === "ratio" ? (inverterSort.dir === "asc" ? "▲" : "▼") : ""}
                    </th>
                    <th className="px-3 py-2 cursor-pointer" onClick={() => toggleInverterSort("start")}>
                      Start {inverterSort.key === "start" ? (inverterSort.dir === "asc" ? "▲" : "▼") : ""}
                    </th>
                    <th className="px-3 py-2 cursor-pointer" onClick={() => toggleInverterSort("stop")}>
                      Stop {inverterSort.key === "stop" ? (inverterSort.dir === "asc" ? "▲" : "▼") : ""}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedInverters.map((inv: any, idx: number) => {
                    const availabilityVal = inv.availability ?? inv.availability_pct ?? null;
                    const availabilityLabel =
                      availabilityVal !== null && availabilityVal !== undefined ? `${Number(availabilityVal).toFixed(2)}%` : "—";
                    const ratioLabel = inv.ratio !== null && inv.ratio !== undefined ? `${inv.ratio.toFixed(1)}%` : "—";
                    const ratioClass =
                      inv.ratio !== null && inv.ratio !== undefined ? (inv.ratio >= 90 ? "text-emerald-600" : "text-amber-600") : "text-slate-500";
                    const startClass = (() => {
                      if (inv.startMinutes == null || startStats.median == null) return "text-slate-700";
                      const delta = inv.startMinutes - startStats.median;
                      if (delta > 20) return "text-rose-600";
                      if (delta > 10) return "text-amber-600";
                      return "text-slate-700";
                    })();

                    return (
                      <tr key={idx} className="border-t">
                        <td className="px-3 py-2">{inv.inverter}</td>
                        <td className={`px-3 py-2 ${prTone(inv.pr)}`}>{inv.pr ? `${inv.pr.toFixed(2)}%` : "—"}</td>
                        <td className={`px-3 py-2 ${ratioTone(availabilityVal)}`}>{availabilityLabel}</td>
                        <td className="px-3 py-2">{inv.energyYield !== null && inv.energyYield !== undefined ? inv.energyYield.toFixed(1) : "—"}</td>
                        <td className={`px-3 py-2 ${ratioClass}`}>{ratioLabel}</td>
                        <td className={`px-3 py-2 ${startClass}`}>{inv.start_time_local || "—"}</td>
                        <td className="px-3 py-2">{inv.stop_time_local || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </ToggleCard>

          <ToggleCard title="Hourly weather data">
            <div className="overflow-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2">Time</th>
                    <th className="px-3 py-2">Temp (°C)</th>
                    <th className="px-3 py-2">Clouds %</th>
                    <th className="px-3 py-2">Wind m/s</th>
                    <th className="px-3 py-2">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {(report?.weather || []).map((w: any, idx: number) => (
                    <tr key={idx} className="border-t">
                      <td className="px-3 py-2">{w.time}</td>
                      <td className="px-3 py-2">{w.temp_c ?? "—"}</td>
                      <td className="px-3 py-2">{w.clouds ?? "—"}</td>
                      <td className="px-3 py-2">{w.wind_speed ?? "—"}</td>
                      <td className="px-3 py-2">{w.description || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ToggleCard>

          <ToggleCard title="Maintenance tickets">
            <div className="space-y-5">
              <TicketGroup title="Active Tickets" tickets={ticketGroups.active} />
              <TicketGroup title="Closed Tickets" tickets={ticketGroups.closed} />
              {ticketGroups.other.length ? <TicketGroup title="Other Tickets" tickets={ticketGroups.other} /> : null}
            </div>
          </ToggleCard>
        </div>
      )}
    </main>
  );
};

const TableCard: React.FC<{ title: string; rows: any[] }> = ({ title, rows }) => (
  <div className="rounded-lg border bg-white p-4 shadow-sm">
    <h3 className="mb-3 text-base font-semibold text-slate-900">{title}</h3>
    <table className="w-full text-left text-sm">
      <thead className="text-slate-500">
        <tr>
          <th className="py-2">Inverter</th>
          <th>PR %</th>
          <th>Availability</th>
          <th>Energy Yield</th>
          <th>Energy / Expected</th>
        </tr>
      </thead>
      <tbody>
        {(rows || []).map((inv: any, idx: number) => {
          const pr = inv.pr ?? null;
          const availability = inv.availability ?? inv.availability_pct ?? null;
          const energyYield = inv.energy_yield_kwh ?? inv.energy_real_kwh ?? inv.energy_yield ?? null;
          const energyExpected = inv.energy_expected_kwh ?? inv.energy_expected ?? null;
          const energyRatio = energyExpected ? (energyYield / energyExpected) * 100 : null;
          const availabilityClass = ratioTone(availability);
          const energyRatioClass = energyRatio !== null ? (energyRatio >= 90 ? "text-emerald-600" : "text-amber-600") : "";
          return (
            <tr key={idx} className="border-t">
              <td className="py-2">{inv.inverter}</td>
              <td className={prTone(pr)}>{pr !== null && pr !== undefined ? `${Number(pr).toFixed(2)}%` : "—"}</td>
              <td className={availabilityClass}>{availability !== null && availability !== undefined ? `${Number(availability).toFixed(1)}%` : "—"}</td>
              <td>{energyYield !== null && energyYield !== undefined ? `${Number(energyYield).toFixed(1)} kWh` : "—"}</td>
              <td className={energyRatioClass}>
                {energyRatio !== null ? `${energyRatio.toFixed(2)}%` : "—"}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export default ReportsPage;
